import type {
  NormalizeWeeklySummaryOptions,
  WeeklySummaryAction,
  WeeklySummaryHighlight,
  WeeklySummaryMetric,
  WeeklySummaryReport,
  WeeklySummaryTone,
  WeeklySummaryView,
} from '../models/weeklySummary.types'

import { resolveWeeklyMetricLogo } from './weeklyMetricLogo'

import { dt } from '@/shared/lib/format'

const DAY_MS = 86_400_000
const STALE_DAYS = 14
const MAX_METRICS = 4
const FAILURE = new Set(['failed', 'error', 'cancelled', 'canceled'])
const WEEKLY_HREF = '/notifications/summary/weekly'
const WEEKDAY: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function payloadOf(value: unknown): Record<string, unknown> | null {
  const body = record(value)
  return body?.status === 'ok' ? record(body.data) : null
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function number(value: unknown): number | null {
  if ((typeof value !== 'number' && typeof value !== 'string') || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function tone(direction: unknown, change: number | null): WeeklySummaryTone {
  const normalized = text(direction).toLowerCase()
  if (normalized === 'up') return 'positive'
  if (normalized === 'down') return 'negative'
  if (change !== null && change > 0) return 'positive'
  if (change !== null && change < 0) return 'negative'
  return 'neutral'
}

function metricsOf(value: unknown, limit = MAX_METRICS): WeeklySummaryMetric[] {
  if (!Array.isArray(value)) return []
  return value.slice(0, limit).flatMap((item, index) => {
    const metric = record(item)
    if (!metric) return []
    const label = text(metric.metric_label)
    const pctChange = number(metric.pct_change)
    const logo = resolveWeeklyMetricLogo({
      logoKey: metric.logo_key,
      platform: metric.platform,
      metricKey: metric.metric_key,
    })
    return [
      {
        id: text(metric.metric_key) || label || String(index),
        label: label || String(index + 1),
        logoKey: logo?.key ?? '',
        logoSrc: logo?.src ?? '',
        value: text(metric.value) || String(metric.value ?? '-'),
        pctChange,
        deltaTone: tone(metric.direction, pctChange),
      },
    ]
  })
}

function integer(value: unknown): number | null {
  const parsed = number(value)
  return parsed !== null && Number.isInteger(parsed) ? parsed : null
}

function highlightsOf(value: unknown): WeeklySummaryHighlight[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((item, index) => {
    const highlight = record(item)
    if (!highlight) return []
    const title = text(highlight.title)
    const detail = text(highlight.detail)
    if (!title || !detail) return []
    return [
      {
        id: `${text(highlight.metric) || 'highlight'}-${index}`,
        kind: text(highlight.type),
        level: text(highlight.level),
        entity: text(highlight.entity),
        title,
        detail,
        metric: text(highlight.metric),
        severity: text(highlight.severity),
      },
    ]
  })
}

function actionsOf(value: unknown): WeeklySummaryAction[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((item, index) => {
    const action = record(item)
    if (!action) return []
    const label = text(action.action)
    const rationale = text(action.rationale)
    if (!label || !rationale) return []
    return [
      {
        id: `${text(action.target_entity) || 'action'}-${index}`,
        action: label,
        rationale,
        targetEntity: text(action.target_entity),
        priority: text(action.priority),
      },
    ]
  })
}

function summaryText(summary: Record<string, unknown>): string {
  const executive = record(summary.executive_summary_html)
  return (
    text(summary.bottom_line_result) ||
    text(executive?.key_takeaway) ||
    text(executive?.plaintext_body) ||
    text(executive?.headline)
  )
}

function isPastCutoff(createdAt: Date, options: NormalizeWeeklySummaryOptions): boolean {
  if (!options.hideAfterWeeklyCutoff) return false
  const zone = options.timeZone || 'UTC'
  const created = dt.getZonedDateTimeParts(createdAt, zone)
  const now = dt.getZonedDateTimeParts(options.now ?? new Date(), zone)
  const createdWeekday = created ? WEEKDAY[created.weekday] : undefined
  if (!created || !now || createdWeekday === undefined) return false
  const cutoff = new Date(
    Date.UTC(created.year, created.month - 1, created.day + (6 - createdWeekday), 9),
  )
  const localNow = Date.UTC(now.year, now.month - 1, now.day, now.hour, now.minute, now.second)
  return localNow >= cutoff.getTime()
}

export function emptyWeeklySummary(): WeeklySummaryView {
  return {
    createdAt: '',
    status: '',
    summaryText: '',
    metrics: [],
    dateRange: { startDate: null, endDate: null },
    shouldRender: false,
    href: WEEKLY_HREF,
  }
}

/** Canonical weekly-digest envelope normalizer for the Summary teaser. */
export function normalizeWeeklySummary(
  value: unknown,
  options: NormalizeWeeklySummaryOptions = {},
): WeeklySummaryView {
  const summary = payloadOf(value)
  if (!summary) return emptyWeeklySummary()
  const created = new Date(text(summary.created_at))
  if (Number.isNaN(created.getTime())) return emptyWeeklySummary()
  const status = text(summary.status).toLowerCase()
  const metrics = metricsOf(summary.kpi_cards)
  const narrative = summaryText(summary)
  const dateRange = record(summary.date_range) ?? record(summary.metadata)
  const now = options.now ?? new Date()
  const stale = now.getTime() - created.getTime() > STALE_DAYS * DAY_MS

  return {
    createdAt: created.toISOString(),
    status,
    summaryText: narrative,
    metrics,
    dateRange: {
      startDate: text(summary.period_start) || text(dateRange?.start_date) || null,
      endDate: text(summary.period_end) || text(dateRange?.end_date) || null,
    },
    shouldRender: Boolean(
      !FAILURE.has(status) &&
      !stale &&
      !isPastCutoff(created, options) &&
      (narrative || metrics.length),
    ),
    href: WEEKLY_HREF,
  }
}

export function emptyWeeklySummaryReport(): WeeklySummaryReport {
  return {
    workflowId: null,
    workspaceId: null,
    executionId: '',
    createdAt: '',
    dateRange: { startDate: null, endDate: null },
    bottomLine: '',
    metrics: [],
    executive: { headline: '', htmlBody: '', keyTakeaway: '', plaintextBody: '' },
    highlights: [],
    actions: [],
    warnings: [],
    shouldRender: false,
  }
}

/** Canonical BE-NTF-15 envelope → full weekly-summary landing-page projection. */
export function normalizeWeeklySummaryReport(
  value: unknown,
  options: NormalizeWeeklySummaryOptions = {},
): WeeklySummaryReport {
  const summary = payloadOf(value)
  if (!summary) return emptyWeeklySummaryReport()
  const teaser = normalizeWeeklySummary(value, options)
  const executive = record(summary.executive_summary_html)

  return {
    workflowId: integer(summary.workflow_id),
    workspaceId: integer(summary.workspace_id),
    executionId: text(summary.execution_id),
    createdAt: teaser.createdAt,
    dateRange: teaser.dateRange,
    bottomLine: text(summary.bottom_line_result),
    metrics: metricsOf(summary.kpi_cards, Number.POSITIVE_INFINITY),
    executive: {
      headline: text(executive?.headline),
      htmlBody: text(executive?.html_body),
      keyTakeaway: text(executive?.key_takeaway),
      plaintextBody: text(executive?.plaintext_body),
    },
    highlights: highlightsOf(summary.highlights),
    actions: actionsOf(summary.actions),
    warnings: Array.isArray(summary.warnings) ? summary.warnings.map(text).filter(Boolean) : [],
    shouldRender: teaser.shouldRender,
  }
}
